import type {CompleteMeshData} from "#template/interfaces/mesh-data"
import type {MeshNodes} from "@cm/render-nodes"
import type {ISceneManager} from "#template/interfaces/scene-manager"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {Subscription} from "rxjs"
import {defer, tap} from "rxjs"
import {hashObject} from "@cm/utils"

const TD = TypeDescriptors

const loadMeshDescriptor = {
    sceneManager: TD.inlet(TD.Identity<ISceneManager>()),
    computeDummyReifiedMeshData: TD.inlet(TD.Boolean),
    data: TD.inlet(TD.Nullable(TD.Identity<Uint8Array>())),
    drcDataObjectId: TD.inlet(TD.Number),
    plyDataObjectId: TD.inlet(TD.Number),
    displaySubdivisionLevel: TD.inlet(TD.Number),
    renderSubdivisionLevel: TD.inlet(TD.Number),
    completeMeshData: TD.outlet(TD.CompleteMeshData),
}

export class LoadMesh implements NodeClassImpl<typeof loadMeshDescriptor, typeof LoadMesh> {
    static descriptor = loadMeshDescriptor
    static uniqueName = "LoadMesh"
    sceneManager!: Inlet<ISceneManager>
    computeDummyReifiedMeshData!: Inlet<boolean>
    data!: Inlet<Uint8Array | null>
    drcDataObjectId!: Inlet<number>
    plyDataObjectId!: Inlet<number>
    displaySubdivisionLevel!: Inlet<number>
    renderSubdivisionLevel!: Inlet<number>
    completeMeshData!: Outlet<CompleteMeshData>

    private pending: Subscription | null = null

    run() {
        this.cleanup()

        if (
            this.sceneManager === NotReady ||
            this.computeDummyReifiedMeshData === NotReady ||
            this.data === NotReady ||
            this.drcDataObjectId === NotReady ||
            this.plyDataObjectId === NotReady ||
            this.displaySubdivisionLevel === NotReady ||
            this.renderSubdivisionLevel === NotReady
        ) {
            this.completeMeshData.emitIfChanged(NotReady)
            return
        }

        const {displaySubdivisionLevel, renderSubdivisionLevel, sceneManager, data, drcDataObjectId, plyDataObjectId} = this

        let renderGeometryGraph: MeshNodes.Mesh = {
            type: "loadMesh",
            data: {
                type: "dataObjectReference",
                dataObjectId: plyDataObjectId,
            },
        }

        if (renderSubdivisionLevel > 0) {
            renderGeometryGraph = {
                type: "subdivide",
                input: renderGeometryGraph,
                levels: renderSubdivisionLevel,
            }
        }

        const displayGeometryGraphLow: MeshNodes.Mesh = {
            type: "loadMesh",
            data: {
                type: "dataObjectReference",
                dataObjectId: drcDataObjectId,
            },
        }

        const displayGeometryGraphHigh: MeshNodes.Mesh | null =
            displaySubdivisionLevel > 0
                ? {
                      type: "subdivide",
                      input: displayGeometryGraphLow,
                      levels: displaySubdivisionLevel,
                  }
                : null

        if (data === null) {
            if (!this.computeDummyReifiedMeshData) {
                throw new Error("LoadMesh node: data is null but dummy reified mesh data computation is disabled")
            }

            // Only provide render graph, empty reified data
            const displayGeometryGraph = displayGeometryGraphHigh ?? displayGeometryGraphLow
            const displayGeometryGraphResources = {[drcDataObjectId]: {contentType: "application/draco", data: new Uint8Array()}}

            this.pending = this.sceneManager.addTask({
                description: `loadDummyMesh(${drcDataObjectId})`,
                task: defer(() =>
                    sceneManager.evaluateMesh(
                        {
                            type: "empty",
                        },
                        {},
                    ),
                ).pipe(
                    tap((empty) => {
                        this.completeMeshData.emitIfChanged({
                            reified: empty,
                            abstract: {
                                contentHash: hashObject(displayGeometryGraph),
                                displayGeometryGraph,
                                renderGeometryGraph,
                                displayGeometryGraphResources,
                            },
                        })
                    }),
                ),
                critical: true,
            })
        } else {
            const displayGeometryGraphResources = {[drcDataObjectId]: {contentType: "application/draco", data}}

            //TODO: unify subdivision application with ProceduralMesh
            const cachedReifiedHigh = displayGeometryGraphHigh ? sceneManager.getCachedMesh(displayGeometryGraphHigh) : null
            if (displayGeometryGraphHigh && cachedReifiedHigh) {
                this.completeMeshData.emitIfChanged({
                    reified: cachedReifiedHigh,
                    abstract: {
                        contentHash: hashObject(displayGeometryGraphHigh),
                        displayGeometryGraph: displayGeometryGraphHigh,
                        renderGeometryGraph,
                        displayGeometryGraphResources,
                    },
                })
            } else {
                this.pending = this.sceneManager.addTask({
                    description: `loadMesh(${plyDataObjectId})`,
                    task: defer(() => sceneManager.evaluateMesh(displayGeometryGraphLow, displayGeometryGraphResources)).pipe(
                        tap((reifiedLow) => {
                            this.completeMeshData.emitIfChanged({
                                reified: reifiedLow,
                                abstract: {
                                    contentHash: hashObject(displayGeometryGraphLow),
                                    displayGeometryGraph: displayGeometryGraphLow,
                                    renderGeometryGraph,
                                    displayGeometryGraphResources,
                                },
                            })
                            if (displayGeometryGraphHigh) {
                                this.pending = sceneManager.addTask({
                                    description: `loadMeshHigh(${plyDataObjectId})`,
                                    task: defer(() => sceneManager.evaluateMesh(displayGeometryGraphHigh, displayGeometryGraphResources)).pipe(
                                        tap((reifiedHigh) => {
                                            this.completeMeshData.emitIfChanged({
                                                reified: reifiedHigh,
                                                abstract: {
                                                    contentHash: hashObject(displayGeometryGraphHigh),
                                                    displayGeometryGraph: displayGeometryGraphHigh,
                                                    renderGeometryGraph,
                                                    displayGeometryGraphResources,
                                                },
                                            })
                                        }),
                                    ),
                                    critical: false,
                                })
                            }
                        }),
                    ),
                    critical: true,
                })
            }
        }
    }

    cleanup() {
        this.pending?.unsubscribe()
        this.pending = null
    }
}
